import * as React from "react";
import { useNavigate } from "react-router-dom";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Menu from "@mui/material/Menu";
import MenuIcon from "@mui/icons-material/Menu";
import Container from "@mui/material/Container";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import MenuItem from "@mui/material/MenuItem";
import { ExpandMore, KeyboardArrowRight } from "@mui/icons-material";
import { toolCategories, toolLinks } from "../Constants/tools_details";
import Chip from "@mui/material/Chip";

const pages = ["Contact Us", "Pricing"];
const settings = ["Profile", "Subscription", "Dashboard", "Logout"];
const pageRoutes = {
  "Contact Us": "/contact",
  Pricing: "/pricing",
};

function Header({ isLoggedIn, user, onLogout, login, loading }) {
  const [anchorElNav, setAnchorElNav] = React.useState(null);
  const [anchorElUser, setAnchorElUser] = React.useState(null);
  const [anchorElTools, setAnchorElTools] = React.useState(null);
  const [subMenuAnchorEl, setSubMenuAnchorEl] = React.useState(null);
  const [currentCategory, setCurrentCategory] = React.useState(null);
  const navigate = useNavigate();

  const handleOpenNavMenu = (event) => setAnchorElNav(event.currentTarget);
  const handleOpenUserMenu = (event) => setAnchorElUser(event.currentTarget);
  const handleOpenToolsMenu = (event) => setAnchorElTools(event.currentTarget);
  
  const handleOpenSubMenu = (event, category) => {
    setCurrentCategory(category);
    setSubMenuAnchorEl(event.currentTarget);
  };

  const handleCloseSubMenu = () => {
    setSubMenuAnchorEl(null);
  };

  const handleCloseToolsMenu = (event) => {
    setAnchorElTools(null);
    const selectedTool = event.currentTarget.textContent;
    if (toolLinks[selectedTool]) {
      navigate(toolLinks[selectedTool]);
    }
  };

  const handleCloseNavMenu = (page) => {
    setAnchorElNav(null);
    if (pageRoutes[page]) {
      navigate(pageRoutes[page]);
    }
  };

  const handleCloseUserMenu = () => setAnchorElUser(null);

  return (
    <AppBar position="sticky" sx={{ backgroundColor: "black" }}>
      <Container maxWidth="xl">
        <Toolbar disableGutters>
          {/* Logo */}
          <Typography
            variant="h6"
            onClick={() => navigate("/")}
            noWrap
            sx={{
              mr: 2,
              display: { xs: "none", md: "flex" },
              fontFamily: "monospace",
              fontWeight: 700,
              letterSpacing: ".3rem",
              color: "white",
              cursor: "pointer",
            }}
          >
            MYFRESHTOOLS
          </Typography>

          {/* Mobile Menu */}
          <Box sx={{ flexGrow: 1, display: { xs: "flex", md: "none" } }}>
            <IconButton
              size="large"
              aria-label="menu"
              onClick={handleOpenNavMenu}
              color="inherit"
            >
              <MenuIcon />
            </IconButton>
            <Menu
              anchorEl={anchorElNav}
              open={Boolean(anchorElNav)}
              onClose={() => setAnchorElNav(null)}
            >
              <MenuItem onClick={handleOpenToolsMenu}>
                <Typography textAlign="center">Explore Tools</Typography>
              </MenuItem>
              <Menu
                anchorEl={anchorElTools}
                open={Boolean(anchorElTools)}
                onClose={() => setAnchorElTools(null)}
              >
                {Object.keys(toolCategories).map((category) => (
                  <MenuItem 
                    key={category}
                    onClick={(e) => handleOpenSubMenu(e, category)}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                      <Typography>{category}</Typography>
                      <KeyboardArrowRight />
                    </Box>
                  </MenuItem>
                ))}
              </Menu>
              <Menu
                anchorEl={subMenuAnchorEl}
                open={Boolean(subMenuAnchorEl)}
                onClose={handleCloseSubMenu}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              >
                {currentCategory && toolCategories[currentCategory].map((tool) => (
                  <MenuItem 
                    key={tool} 
                    onClick={(e) => {
                      handleCloseSubMenu();
                      handleCloseToolsMenu(e);
                    }}
                  >
                    {tool}
                  </MenuItem>
                ))}
              </Menu>
              {pages.map((page) => (
                <MenuItem key={page} onClick={() => handleCloseNavMenu(page)}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography textAlign="center">{page}</Typography>
                    {page === "Pricing" && (
                      <Chip
                        label="50% OFF"
                        size="small"
                        sx={{
                          ml: 1,
                          backgroundColor: "#FFC107",
                          color: "black",
                          fontWeight: 'bold',
                          fontSize: '0.7rem',
                          height: '20px'
                        }}
                      />
                    )}
                  </Box>
                </MenuItem>
              ))}
            </Menu>
          </Box>

          {/* Desktop Menu */}
          <Box sx={{ flexGrow: 1, display: { xs: "none", md: "flex" } }}>
            <Box sx={{ position: 'relative' }}>
              <Button
                onClick={handleOpenToolsMenu}
                endIcon={<ExpandMore />}
                sx={{ my: 2, color: "white" }}
              >
                Explore Tools
              </Button>
              <Menu
                anchorEl={anchorElTools}
                open={Boolean(anchorElTools)}
                onClose={() => setAnchorElTools(null)}
              >
                {Object.keys(toolCategories).map((category) => (
                  <MenuItem 
                    key={category}
                    onMouseEnter={(e) => handleOpenSubMenu(e, category)}
                    sx={{ justifyContent: 'space-between' }}
                  >
                    <Typography>{category}</Typography>
                    <KeyboardArrowRight />
                  </MenuItem>
                ))}
              </Menu>
              <Menu
                anchorEl={subMenuAnchorEl}
                open={Boolean(subMenuAnchorEl)}
                onClose={handleCloseSubMenu}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                MenuListProps={{ 
                  onMouseLeave: handleCloseSubMenu,
                  autoFocus: false 
                }}
                sx={{ pointerEvents: 'none' }}
              >
                <Box sx={{ pointerEvents: 'auto' }}>
                  {currentCategory && toolCategories[currentCategory].map((tool) => (
                    <MenuItem 
                      key={tool} 
                      onClick={(e) => {
                        handleCloseSubMenu();
                        handleCloseToolsMenu(e);
                      }}
                    >
                      {tool}
                    </MenuItem>
                  ))}
                </Box>
              </Menu>
            </Box>
            {pages.map((page) => (
              <Box key={page} sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <Button
                  onClick={() =>
                    navigate(page === "Contact Us" ? "/contact" : "/pricing")
                  }
                  sx={{ my: 2, color: "white" }}
                >
                  {page}
                </Button>
                {page === "Pricing" && (
                  <Chip
                    label="50% OFF"
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: '5px',
                      right: '-10px',
                      backgroundColor: "#FFC107",
                      color: "black",
                      fontWeight: 'bold',
                      fontSize: '0.7rem',
                      height: '20px',
                      transform: 'rotate(15deg)'
                    }}
                  />
                )}
              </Box>
            ))}
          </Box>

          {/* Google Login Button or User Avatar */}
          <Box sx={{ flexGrow: 0 }}>
            {isLoggedIn ? (
              <Tooltip title="Open settings">
                <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                  <Avatar alt="User Avatar" src={user?.profile_pic || ""} />
                </IconButton>
              </Tooltip>
            ) : (
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Button
                  sx={{ my: 2, color: "white" }}
                  onClick={() =>
                    (window.location.href = `${process.env.REACT_APP_FRONTEND_URL}/welcome/login`)
                  }
                >
                  Sign in
                </Button>
                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: "#FFC107",
                    color: "black",
                    borderRadius: "20px",
                    textTransform: "none",
                    px: 3,
                    py: 1,
                    fontWeight: 600,
                    fontSize: "0.9rem",
                    boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.15)",
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      backgroundColor: "#e0a800",
                      transform: "translateY(-1px)",
                    },
                    "&:active": {
                      transform: "scale(0.97)",
                    },
                  }}
                  onClick={login}
                >
                  Try It Free
                </Button>
              </Box>
            )}

            <Menu
              anchorEl={anchorElUser}
              open={Boolean(anchorElUser)}
              onClose={handleCloseUserMenu}
            >
              {settings.map((setting) => (
                <MenuItem
                  key={setting}
                  onClick={() => {
                    handleCloseUserMenu();
                    if (setting === "Logout") {
                      onLogout();
                    } else if (setting === "Dashboard") {
                      navigate("/dashboard");
                    } else if (setting === "Profile") {
                      navigate("/profile");
                    } else if (setting === "Subscription") {
                      navigate("/subscription");
                    }
                  }}
                >
                  <Typography textAlign="center">{setting}</Typography>
                </MenuItem>
              ))}
            </Menu>
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
}

export default Header;